import {
  Box,
  Paper,
  Typography,
} from "@mui/material";
import React, { useContext } from "react";
import UserContext from "../../context/User";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import ApartmentOutlinedIcon from "@mui/icons-material/ApartmentOutlined";
import MeetingRoomIcon from "@mui/icons-material/MeetingRoom";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useTranslation } from 'react-i18next';

function PersonalCard({ toggleModal, openAddressDetail, location, deliveryInstructions, setDeliveryInstructions }) {
  const { t } = useTranslation();
  const { profile } = useContext(UserContext);

  // ── Type + spacing scale ────────────────────────────────────────────────
  // One rhythm for the card: 12px uppercase eyebrow, 14px rows on a 12px beat,
  // muted secondary line under each value. Labels ("Name :", "Email :") are
  // gone — an icon carries the meaning, the way DoorDash lists the address,
  // dropoff choice and phone number.
  const INK = "#111827";
  const MUTED = "#6B7280";
  const LINE = "#E8EAED";
  const ACCENT = "#FF3008";

  const eyebrowSx = {
    fontSize: "0.7rem",
    fontWeight: 800,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: MUTED,
  };

  const DROPOFF_LABELS = {
    LEAVE_AT_DOOR: "Leave at my door",
    MEET_AT_DOOR: "Meet me at the door",
    HAND_IT_TO_ME: "Hand it to me",
  };

  // Structured unit details captured when the address was saved.
  const unitParts = [
    location?.label,
    location?.apartment,
    location?.buildingName,
    location?.entryCode && `Code ${location.entryCode}`,
  ].filter(Boolean);

  const dropoffLabel = DROPOFF_LABELS[location?.dropoffOption];

  // One row = icon, value, optional muted second line, optional chevron when
  // tapping it goes somewhere.
  // The address row opens the picker — changing where it goes usually means
  // choosing a different saved address. The dropoff row opens the structured
  // form for THIS address, since that's the only place the option is set.
  const editAddress = openAddressDetail || toggleModal;

  const Row = ({ Icon, value, secondary, onClick, muted }) => (
    <Box
      onClick={onClick}
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: 1.5,
        py: 1.5,
        borderTop: `1px solid ${LINE}`,
        cursor: onClick ? "pointer" : "default",
        transition: "background-color .15s ease",
        ...(onClick ? { "&:hover": { backgroundColor: "#FAFAFB" } } : {}),
      }}
    >
      <Icon sx={{ fontSize: "1.25rem", color: MUTED, mt: "1px", flexShrink: 0 }} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: "0.875rem",
            fontWeight: 600,
            lineHeight: 1.45,
            color: muted ? MUTED : INK,
            wordBreak: "break-word",
          }}
        >
          {value}
        </Typography>
        {secondary ? (
          <Typography sx={{ fontSize: "0.8rem", color: MUTED, lineHeight: 1.45, mt: 0.25 }}>
            {secondary}
          </Typography>
        ) : null}
      </Box>
      {onClick ? (
        <ChevronRightIcon sx={{ fontSize: "1.25rem", color: "#C7CBD1", flexShrink: 0 }} />
      ) : null}
    </Box>
  );

  return (
    <Paper
      elevation={0}
      // Flat: this sits inside checkout's Deliver to card, under the map, so it
      // must not draw a second card of its own.
      sx={{
        background: "#fff",
        borderRadius: 0,
        boxShadow: "none",
        px: { xs: 2, sm: 2.5 },
        py: 2.5,
      }}
    >
      <Box display="flex" alignItems="center" sx={{ gap: 1, mb: 1.5 }}>
        <LocationOnIcon sx={{ fontSize: "1.1rem", color: ACCENT }} />
        <Typography sx={eyebrowSx}>{t("contactInfo")}</Typography>
      </Box>

      <Row
        Icon={ApartmentOutlinedIcon}
        value={location?.deliveryAddress ?? ""}
        secondary={unitParts.length ? unitParts.join(" · ") : null}
        onClick={toggleModal}
      />
      {/* Handover choice gets its own row, the way DoorDash surfaces
          "Leave at apartment door". It and the address row above both open the
          address form — which is why there's no separate Change Address
          button: the row you want to change is the thing you tap. */}
      <Row
        Icon={MeetingRoomIcon}
        value={dropoffLabel || t("dropoffOption")}
        muted={!dropoffLabel}
        onClick={editAddress}
      />
      <Row Icon={PersonOutlineIcon} value={profile?.name ?? ".."} />
      <Row Icon={PhoneOutlinedIcon} value={profile?.phone ?? ""} />
      <Row Icon={MailOutlineIcon} value={profile?.email ?? ""} />

      {setDeliveryInstructions && (
        <Box sx={{ mt: 2.5, pt: 2.5, borderTop: `1px solid ${LINE}` }}>
          <Typography sx={{ ...eyebrowSx, mb: 1.25 }}>Delivery Instructions</Typography>
          <textarea
            value={deliveryInstructions}
            onChange={(e) => setDeliveryInstructions(e.target.value)}
            placeholder="E.g. Leave at door, ring bell, gate code #1234..."
            rows={3}
            style={{
              width: "100%",
              border: `1.5px solid ${LINE}`,
              backgroundColor: "#F7F8FA",
              borderRadius: 12,
              padding: "12px 14px",
              fontSize: "0.875rem",
              lineHeight: 1.5,
              color: INK,
              fontFamily: "inherit",
              resize: "vertical",
              outline: "none",
              boxSizing: "border-box",
            }}
          />
        </Box>
      )}
    </Paper>
  );
}

export default React.memo(PersonalCard);
